import { makeAutoObservable, runInAction } from 'mobx'
import { isCancel } from 'axios'
import { RequestCancelAxios } from 'shared/api/lib'
import { logger, removeSpacesFromNumericString } from 'shared/lib'
import { type IntegrationKey } from 'entities/Integrations'
import { type IParamsContactsSearch, type IResponseContactAll } from 'entities/Contacts/api/types'
import { type IContactAll } from 'entities/Contacts'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IResultsContactsByIntegrationFetch } from 'pages/conversations/pages/search/ui/Results/store/types'

export class ResultsContactsByIntegrationStore {
  private _cancelRequest = new RequestCancelAxios()
  private _itemsMap: Map<IResponseContactAll['id'], IContactAll> = new Map()
  private _loading = false
  private _loadingMore = false
  private _total = 0
  private _pagination: IParamsContactsSearch = {
    per_page: 20,
    page: 1,
    skip_opted_out: 0,
  }

  constructor(private _key: IntegrationKey, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get loadingMore() {
    return this._loadingMore
  }

  get loading() {
    return this._loading
  }

  get total() {
    return this._total
  }

  get cancelRequest() {
    return this._cancelRequest
  }

  get hasItems() {
    return Boolean(this._itemsMap.size)
  }

  get hasMore() {
    const loadedCount = this._pagination.page * (this._pagination.per_page || 10)

    return loadedCount < this._total
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  dispose = () => {
    this._cancelRequest.reset()
    this._itemsMap.clear()
    this._loading = false
    this._loadingMore = false
    this._total = 0
    this._pagination = {
      per_page: 20,
      page: 1,
      skip_opted_out: 0,
    }
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  fetch = async ({ more = false, search = '' }: IResultsContactsByIntegrationFetch) => {
    try {
      runInAction(() => {
        if (more) this._loadingMore = true
        else this._loading = true
      })

      const params = this._pagination

      if (more) {
        params.page += 1
      }

      if (search) {
        this.resetItems()
        params.term = removeSpacesFromNumericString(search)
        params.page = 1
      }

      const response = await this._entities.contactsStore.getIntegrationSearchContact(
        this._key,
        params,
        this._cancelRequest.token
      )

      if (response?.contacts) this.addItems(response?.contacts)

      runInAction(() => {
        this._total = response?.total || 0
        this._loadingMore = false
        this._loading = false
      })
    } catch (e) {
      if (!isCancel(e)) {
        runInAction(() => {
          this._loadingMore = false
          this._loading = false
        })
      }

      logger.error(e)
    }
  }

  addItems = (items: IContactAll[]) => {
    items.forEach((item) => {
      this._itemsMap.set(item.id, item)
    })
  }

  resetItems = () => {
    this._itemsMap.clear()
  }
}
